import { motion } from "framer-motion";
import { FileText, ArrowUpRight, Stethoscope, BookMarked } from "lucide-react";
import AbstractShapes from "@/components/landing/AbstractShapes";

const JOURNALS = [
  {
    id: "ijmrcr",
    icon: BookMarked,
    short: "IJMRCR",
    title: "International Journal of Medical Reviews and Case Reports",
    issn: "ISSN: 2534-9821",
    submitUrl: "https://www.ejmanager.com/my/ijmrcr/",
    trackUrl: "https://www.ejmanager.com/my/ijmrcr/submit.php?isl=track",
    scope: "Medical reviews · Case reports · Clinical research",
    accent: "var(--sci-teal)",
  },
  {
    id: "ijsm",
    icon: Stethoscope,
    short: "IJSM",
    title: "International Journal of Surgery and Medicine",
    issn: "E-ISSN: 2367-699X · P-ISSN: 2367-7414",
    submitUrl: "https://www.ejmanager.com/my/ijsm/",
    trackUrl: "https://www.ejmanager.com/my/ijsm/submit.php?isl=track",
    scope: "Surgery · Perioperative · Multidisciplinary care",
    accent: "var(--sci-gold)",
  },
];

export default function Submission() {
  return (
    <section
      id="submit"
      data-testid="submission-section"
      className="relative py-24 md:py-32 bg-white border-y border-[var(--sci-border)] overflow-hidden"
    >
      <AbstractShapes
        variant="arcs"
        tone="ink"
        className="right-[-100px] top-0 w-[400px] h-[400px] opacity-50"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
          <div className="lg:col-span-7">
            <div className="overline text-[var(--sci-teal)] mb-5">
              · 06 / Submit Your Manuscript
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[var(--sci-navy)] tracking-tight leading-[1.02]">
              Choose your <em className="not-italic text-[var(--sci-teal)]">journal</em>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--sci-text-muted)] leading-relaxed text-base md:text-lg">
            Each journal maintains its own editorial workflow on the
            <span className="font-mono-sci text-[var(--sci-navy)] text-sm mx-1.5">
              eJManager
            </span>
            platform. Select your title to open the corresponding submission
            portal.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {JOURNALS.map((j, i) => {
            const Icon = j.icon;
            return (
              <motion.div
                key={j.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.1 * i }}
                data-testid={`submit-card-${j.id}`}
                className="group relative bg-white border border-[var(--sci-border)] card-lift p-8 md:p-10 overflow-hidden"
              >
                <div
                  className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                  style={{ background: j.accent }}
                />

                <div className="flex items-start justify-between mb-7">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 border border-[var(--sci-navy)]/15 flex items-center justify-center text-[var(--sci-teal)]">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="overline text-[var(--sci-text-muted)]">
                        {j.short}
                      </div>
                      <div className="font-mono-sci text-[11px] tracking-wider text-[var(--sci-navy)] mt-1">
                        {j.issn}
                      </div>
                    </div>
                  </div>
                  <div className="font-mono-sci text-xs text-[var(--sci-text-muted)]">
                    0{i + 1}
                  </div>
                </div>

                <h3 className="font-serif-display text-2xl md:text-3xl text-[var(--sci-navy)] tracking-tight leading-tight">
                  {j.title}
                </h3>
                <p className="mt-4 text-sm text-[var(--sci-text-muted)] leading-relaxed">
                  {j.scope}
                </p>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  <a
                    href={j.submitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`submit-cta-${j.id}`}
                    className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm tracking-wide"
                  >
                    <FileText size={14} />
                    Submit
                    <ArrowUpRight size={14} />
                  </a>
                  <a
                    href={j.trackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`track-cta-${j.id}`}
                    className="btn-secondary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm tracking-wide"
                  >
                    Track manuscript
                  </a>
                </div>

                <div className="mt-6 font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)]">
                  PORTAL · ejmanager.com/my/{j.id}/
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 text-xs text-[var(--sci-text-muted)] max-w-3xl leading-relaxed">
          <span className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-teal)] mr-2">
            FIRST TIME?
          </span>
          You will be prompted to create an eJManager account on the journal
          portal. Please ensure your manuscript follows the relevant{" "}
          <a
            href="#standards"
            className="text-[var(--sci-navy)] underline decoration-dotted hover:text-[var(--sci-teal)]"
          >
            EQUATOR reporting guideline
          </a>{" "}
          before submission.
        </div>
      </div>
    </section>
  );
}
